import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { watchAuthState, signOutUser } from '../services/authService';
import { getUserProfileByUid } from '../services/userProfileService';
import { postToRN, isInRnWebView, requestPushToken, onPushToken, listenWebviewMessages, appendRnLog } from '../bridge/webviewBridge';
import { saveFcmToken, removeFcmToken } from '../services/fcmTokenService';
import { auth } from '../firebase/config';
import { APP_CONFIG } from '../config/appConfig';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Apple 소셜 로그인 결과를 AuthContext에서 직접 처리 (WebView 리로드 대비)
async function processAppleSigninResult(msg) {
  if (msg.provider !== 'apple' || !msg.identityToken) return false;
  const cfUrl = APP_CONFIG.appleCustomTokenUrl;
  if (!cfUrl) return false;
  try {
    const res = await fetch(cfUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identityToken: msg.identityToken, displayName: msg.displayName || '' }),
    });
    const d = await res.json();
    if (d.customToken) {
      await signInWithCustomToken(auth, d.customToken);
      return true;
    }
  } catch (err) {
    console.error('[AuthContext] Apple 로그인 처리 실패:', err);
  }
  return false;
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const pendingFcmToken = useRef(null);
  const signinHandled = useRef(false);

  // localStorage에 보류된 소셜 로그인 결과 복구 (WebView 리로드 대비)
  useEffect(() => {
    if (!isInRnWebView()) return;
    try {
      const raw = localStorage.getItem('__pendingSigninResult');
      if (!raw) return;
      localStorage.removeItem('__pendingSigninResult');
      const data = JSON.parse(raw);
      const msg = data?.payload || data;
      if (msg?.success && msg?.provider === 'apple' && !signinHandled.current) {
        signinHandled.current = true;
        processAppleSigninResult(msg).finally(() => { signinHandled.current = false; });
      }
    } catch {}
  }, []);

  // 메시지 리스너로 Apple 로그인 결과 실시간 처리 (LoginPage에서 못 잡은 경우)
  useEffect(() => {
    if (!isInRnWebView()) return;
    const off = listenWebviewMessages((msg) => {
      if (msg.type === 'SIGNIN_RESULT' && msg.success && msg.provider === 'apple') {
        try { localStorage.removeItem('__pendingSigninResult'); } catch {}
        if (!signinHandled.current) {
          signinHandled.current = true;
          processAppleSigninResult(msg).finally(() => { signinHandled.current = false; });
        }
      }
    });
    return off;
  }, []);

  useEffect(() => {
    const unsubscribe = watchAuthState(async (firebaseUser) => {
      if (firebaseUser) {
        setCurrentUser(firebaseUser);
        try {
          const profile = await getUserProfileByUid(firebaseUser.uid);
          setUserData(profile || {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName || '',
            phoneE164: '',
            phoneVerified: false,
            provider: '',
            status: '',
          });
        } catch (err) {
          console.error('프로필 로드 실패:', err);
          setUserData({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName || '',
            phoneE164: '',
            phoneVerified: false,
            provider: '',
            status: '',
          });
        }

        // RN WebView일 때 FCM 토큰 요청
        if (isInRnWebView()) {
          requestPushToken();
        }
      } else {
        setCurrentUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // FCM 토큰 수신 → Firestore 저장 (userData.uid = Firestore 문서 ID 사용)
  useEffect(() => {
    if (!isInRnWebView()) return;
    const off = onPushToken((token, platform) => {
      pendingFcmToken.current = { token, platform };
      const firestoreUid = userData?.uid || currentUser?.uid;
      if (firestoreUid) {
        console.log('[FCM] 토큰 즉시 저장:', firestoreUid, token?.slice(0, 20));
        saveFcmToken(firestoreUid, token, platform).catch(() => {});
      } else {
        console.log('[FCM] uid 없음 — 토큰 보류:', token?.slice(0, 20));
      }
      setDebugInfo(prev => ({ ...prev, fcmToken: token, platform }));
    });
    return off;
  }, [currentUser, userData]);

  // userData 로드 완료 시 보류중인 FCM 토큰 저장 + 재요청
  useEffect(() => {
    if (!isInRnWebView() || !userData?.uid) return;
    // 보류중인 토큰이 있으면 저장
    if (pendingFcmToken.current) {
      const { token, platform } = pendingFcmToken.current;
      console.log('[FCM] 보류 토큰 저장:', userData.uid, token?.slice(0, 20));
      saveFcmToken(userData.uid, token, platform).catch(() => {});
    } else {
      // 토큰이 아직 없으면 다시 요청
      console.log('[FCM] userData 로드됨 — 토큰 재요청');
      requestPushToken();
    }
  }, [userData?.uid]);

  // RN 브릿지 메시지 로깅 (디버그용)
  useEffect(() => {
    if (!isInRnWebView()) return;
    const off = listenWebviewMessages((msg) => {
      const type = msg.type || '';
      const pp = { ...msg };
      delete pp.type;
      const tokenHint = pp.token ? `tk:${String(pp.token).slice(0,15)}…` : '';
      appendRnLog(type, Object.keys(pp), tokenHint);

      if (type === 'DEBUG_MODE') {
        setDebugMode(!!msg.enabled);
      }
      if (type === 'PUSH_TOKEN' || type === 'FCM_TOKEN') {
        setDebugInfo(prev => ({ ...prev, fcmToken: msg.token, platform: msg.platform }));
      }
    });
    return off;
  }, []);

  const refreshUser = useCallback(async () => {
    if (!currentUser) return;
    try {
      const profile = await getUserProfileByUid(currentUser.uid);
      if (profile) setUserData(profile);
    } catch (err) {
      console.error('refreshUser 실패:', err);
    }
  }, [currentUser]);

  const login = (data) => {
    setUserData(data);
  };

  const logout = async () => {
    // 로그아웃 시 FCM 토큰 삭제 (Firestore 문서 ID 사용)
    const firestoreUid = userData?.uid || currentUser?.uid;
    if (firestoreUid) {
      removeFcmToken(firestoreUid).catch(() => {});
    }
    if (isInRnWebView()) postToRN("START_SIGNOUT");
    await signOutUser();
    setCurrentUser(null);
    setUserData(null);
  };

  const isLoggedIn = !!(currentUser && userData);

  const value = {
    currentUser,
    userData,
    loading,
    isLoggedIn,
    login,
    logout,
    refreshUser,
    debugMode,
    debugInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
